import { curriculum } from '@/content/curriculum';
import type { Localized } from '@/content/schema';
import type { MasteryRecord } from '@/domain/records';
import type { Translate, TKey } from '@/i18n/translate';
import type { Recommendation } from './recommend';

const REASON_KEYS: Record<string, TKey> = {
  notStarted: 'mastery.reason.notStarted',
  gatheringEvidence: 'mastery.reason.gatheringEvidence',
  consistentAcrossDays: 'mastery.reason.consistentAcrossDays',
  needsConfirmation: 'mastery.reason.needsConfirmation',
  mostlyCorrect: 'mastery.reason.mostlyCorrect',
  frequentMistakes: 'mastery.reason.frequentMistakes',
  mixedResults: 'mastery.reason.mixedResults',
};

/** Explains a mastery estimate in words, from the numbers behind it. */
export function masteryReasonText(t: Translate, reason: MasteryRecord['reason']): string {
  const key = REASON_KEYS[reason.code];
  return key ? t(key, reason.params) : '';
}

/** Explains why an activity is recommended. */
export function recommendationText(
  t: Translate,
  l: (text: Localized) => string,
  rec: Pick<Recommendation, 'kind' | 'reason' | 'becauseOfId'>,
): string {
  const params = rec.reason.params;
  switch (rec.kind) {
    case 'review':
      return t('recommend.reason.review', { count: Number(params.days ?? 0) });
    case 'prerequisite': {
      const because = curriculum.objective(String(params.objective ?? rec.becauseOfId ?? ''));
      return t('recommend.reason.prerequisite', { objective: because ? l(because.title) : '' });
    }
    case 'easier':
      return t('recommend.reason.easier', { percent: Number(params.percent ?? 0) });
    case 'practice':
      return t('recommend.reason.practice', { percent: Number(params.percent ?? 0) });
    case 'next':
      return t('recommend.reason.next');
    case 'start':
      return t('recommend.reason.start');
  }
}
